import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, Injectable, NgModule } from '@angular/core';
import { OrderFlowErrorHandler } from './core/services/telemetry/order-flow-error-handler';
import { ApiFailInterceptor } from './core/services/telemetry/api-fail.interceptor';
import { SentryService } from './core/services/telemetry/sentry/sentry.service';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { DefaultUrlSerializer, UrlSerializer, UrlTree } from '@angular/router';

@Injectable()
class RestaurantUrlSerializer extends DefaultUrlSerializer {
    parse(url: string): UrlTree {
        const parts = url.split('?');
        const segments = parts[0].split('/');
        if (segments.length > 1) segments[1] = segments[1].toLowerCase();
        parts[0] = segments.join('/');
        return super.parse(parts.join('?'));
    }
}


import { CoreModule } from './core/core.module';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { TokenInterceptor } from './core/interceptor/token.interceptor';
import { MenuPageModule } from './pages/menu/menu.module';
import { AppMaskModule } from './core/mask/app-mask.module'
import { UntypedFormBuilder } from '@angular/forms';
import { MpImageViewerModule } from 'src/app/shared/components/image-viewer/image-viewer.module';

import { PipesModule } from './shared/pipe/pipe.module';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { esperarTraducoesDaPaginaDoServidor } from './core/i18n/traducoes-do-servidor';
import { RedirectWhatsappPageModule } from './pages/payment/redirect-whatsapp/redirect-whatsapp.module';
import { FollowOrderPageModule } from './pages/payment/follow-order/follow-order.module';
import { TimeoutPageModule } from './pages/timeout/timeout.module';
import { ModalOutletComponent } from './shared/components/modal-outlet/modal-outlet.component';
import { ModalPageModule } from './pages/modal/modal.module';

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  declarations: [
    AppComponent,
    ModalOutletComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,

    CoreModule,

    MenuPageModule,
    TimeoutPageModule,

    AppMaskModule,
    MpImageViewerModule,
    RedirectWhatsappPageModule,
    FollowOrderPageModule,
    ModalPageModule,

  ],
  providers: [
    // Hidratação do HTML pré-renderizado (MVP de SSR no yellow): o cliente reaproveita o DOM
    // que veio do servidor em vez de refazê-lo. Sem HTML do servidor — todo boot fora das
    // lojas pré-renderizadas — ela não tem o que hidratar e o boot é o de sempre.
    provideClientHydration(),
    { provide: UrlSerializer, useClass: RestaurantUrlSerializer },
    TranslateModule,
    CurrencyPipe,
    UntypedFormBuilder,
    PipesModule,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    // Telemetria de saúde (health_15m → alertas js_error/api_fail no
    // order-flow-monitor). O handler delega o console.error default; o
    // interceptor só observa falhas (0/5xx/timeout) de endpoints críticos.
    {
      provide: ErrorHandler,
      useClass: OrderFlowErrorHandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiFailInterceptor,
      multi: true
    },
    // NÃO registramos o LegacyFloatingLabelService (APP_INITIALIZER) que a master registra:
    // ele é compat Ionic 7+ para o pattern <ion-item><ion-label position><ion-input>, e esta
    // branch não tem Ionic — não há um único <ion-item> nos templates nem CSS que responda a
    // `item-has-focus`/`item-has-value`. Registrá-lo custaria dois listeners delegados no
    // document rodando `closest()` a cada tecla, para nunca casar nada. O serviço e seu spec
    // seguem no repo (compilam e passam) para não divergir da master à toa.
    // Sentry: o initializer só DECIDE e AGENDA (síncrono, sem promise — não
    // atrasa o bootstrap em nada). O SDK entra por chunk lazy, no primeiro idle
    // depois do `load`, e só nos devices dentro da coorte. Ver SENTRY.md.
    {
      provide: APP_INITIALIZER,
      useFactory: (svc: SentryService) => () => svc.boot(),
      deps: [SentryService],
      multi: true
    },
    // Página do SSR: as traduções não vêm mais no estado — espera o JSON (pré-carregado
    // pelo <link rel="preload"> do servidor) antes de hidratar, com teto de 2,5 s.
    // Sem SSR, não espera nada. Ver traducoes-do-servidor.ts.
    {
      provide: APP_INITIALIZER,
      useFactory: esperarTraducoesDaPaginaDoServidor,
      deps: [DOCUMENT, TranslateService],
      multi: true
    }
  ],
  exports: [
    TranslateModule,
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
